import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS } from '../config/constants';
import { LevelResult } from '../types';
import { SaveData } from '../systems/SaveData';
import { getLevel, LEVELS } from '../data/levels';
import { Sfx } from '../systems/Sfx';
import { calculateStars, isLevelPassed } from '../systems/Scoring';
import { trackLevelCompleted } from '../systems/Telemetry';
import { shouldShowInterstitialAfterLevel, showInterstitial } from '../systems/Ads';

export class LevelCompleteScene extends Phaser.Scene {
  private result!: LevelResult;

  constructor() {
    super('LevelComplete');
  }

  init(data: { result: LevelResult }): void {
    const level = getLevel(data.result.levelId);
    this.result = {
      ...data.result,
      // Recalculate at the scene boundary so progression and presentation can
      // never disagree with the authoritative level thresholds.
      stars: calculateStars(data.result.score, level.starThresholds)
    };
    SaveData.recordLevelResult(this.result);
    trackLevelCompleted(this.result);
  }

  create(): void {
    const passed = isLevelPassed(this.result.stars);
    const level = getLevel(this.result.levelId);
    const nextLevel = passed ? LEVELS.find((candidate) => candidate.id === this.result.levelId + 1) : undefined;
    if (passed) {
      Sfx.levelComplete();
      for (let i = 0; i < this.result.stars; i++) {
        this.time.delayedCall(300 + i * 200, () => Sfx.starAward());
      }
    } else {
      Sfx.levelFail();
    }

    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_christmastown').setAlpha(0.6);
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'ui_panel');

    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 120, passed ? 'Level Complete!' : 'Try Again!', {
        fontFamily: 'Georgia, serif',
        fontSize: '40px',
        color: '#ffffff'
      })
      .setOrigin(0.5);

    const starSpacing = 60;
    for (let i = 0; i < 3; i++) {
      const key = i < this.result.stars ? 'ui_star_filled' : 'ui_star_empty';
      this.add
        .image(GAME_WIDTH / 2 + (i - 1) * starSpacing, GAME_HEIGHT / 2 - 60, key)
        .setScale(1.2);
    }

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2 + 10,
        passed ? `Score: ${this.result.score}` : `Score: ${this.result.score} / ${level.starThresholds[0]} to pass`,
        {
        fontFamily: 'Arial',
        fontSize: '26px',
        color: '#ffffff'
        }
      )
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 45, `Best Combo: ${this.result.bestCombo}`, {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#cccccc'
      })
      .setOrigin(0.5);

    this.createButton(nextLevel ? GAME_WIDTH / 2 - 130 : GAME_WIDTH / 2, GAME_HEIGHT / 2 + 110, 'Retry', () =>
      this.scene.start('Game', { levelId: this.result.levelId })
    );

    if (nextLevel) {
      this.createButton(GAME_WIDTH / 2 + 130, GAME_HEIGHT / 2 + 110, 'Next', () => {
        if (!shouldShowInterstitialAfterLevel(this.result.levelId, this.result.stars)) {
          this.scene.start('Game', { levelId: nextLevel.id });
          return;
        }

        void showInterstitial({
          placement: `after_level_${this.result.levelId}`,
          beforeAd: () => this.sound.pauseAll(),
          afterAd: () => this.sound.resumeAll(),
          showPlaceholder: (complete) => {
            this.scene.pause();
            this.scene.launch('AdPlaceholder', {
              complete: () => {
                this.scene.resume();
                complete();
              }
            });
          }
        }).then(() => this.scene.start('Game', { levelId: nextLevel.id }));
      });
    }

    const backText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 170, 'Level Select', {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#ffffff'
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backText.on('pointerdown', () => {
      Sfx.buttonTap();
      this.scene.start('LevelSelect');
    });
  }

  private createButton(x: number, y: number, label: string, onClick: () => void): void {
    const button = this.add
      .rectangle(x, y, 180, 56, COLORS.fusionGold, 1)
      .setStrokeStyle(3, COLORS.outline)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(x, y, label, { fontFamily: 'Arial', fontSize: '22px', color: '#2e2a38', fontStyle: 'bold' })
      .setOrigin(0.5);
    button.on('pointerover', () => button.setScale(1.05));
    button.on('pointerout', () => button.setScale(1));
    button.on('pointerdown', () => {
      Sfx.buttonTap();
      onClick();
    });
  }
}
